import { useCallback, useState } from 'react'
import getCroppedImg from './useCropImage'
import { Stack } from '@mui/material'
import Cropper, { Area, Point } from 'react-easy-crop'
import { Dropzone, EFButton, EFFooterDialogWrapper, EFTypography } from '~/components'
import { ALL_NS } from '~/i18n/namespaces'
import { useTranslation } from 'react-i18next'
import style from './ImportFilePopup.module.scss'
import classNames from 'classnames/bind'
import { currentTheme } from '~/redux/features'
import { useAppSelector } from '~/app/hooks'
import Resizer from 'react-image-file-resizer'
import { useModal } from 'mui-modal-provider'
import { ConfirmationPopup } from '../ConfirmationPopup'

const cx = classNames.bind(style)

interface IImageEditorPopupProps {
  onSubmit: (file: File) => void
  handleClose: () => void
  isSaving?: boolean
}

async function dataUrlToFile(dataUrl: string, fileName: string): Promise<File> {
  const res: Response = await fetch(dataUrl)
  const blob: Blob = await res.blob()
  return new File([blob], fileName, { type: 'image/png' })
}

export function ImageEditorPopup({
  onSubmit,
  handleClose,
  isSaving
}: IImageEditorPopupProps) {
  const { t } = useTranslation(ALL_NS)
  const theme = useAppSelector(currentTheme)
  const { showModal } = useModal()

  const [dataUrl, setDataUrl] = useState<string>()
  const [fileName, setFileName] = useState<string>('')
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedImage, setCroppedImage] = useState<any>(null)

  const onDrop = useCallback((acceptedFiles: File[]) => {
    acceptedFiles.forEach(file => {
      if (file.size > 2097152) {
        showModal(ConfirmationPopup, {
          actionType: 'Close',
          children: t('Message_MaximumImageFileSize'),
          title: t('MessageTitle_Error'),
          onClose: () => {}
        })
        return
      }

      const reader = new FileReader()
      setFileName(file.name)
      reader.onload = () => {
        setDataUrl(reader.result?.toString())
      }
      reader.readAsDataURL(file)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const onCropComplete = async (_: Area, croppedAreaPixels: Area) => {
    const croppedImage = await getCroppedImg(dataUrl ?? '', croppedAreaPixels, 0)
    setCroppedImage(croppedImage)
  }

  async function onChangeImage(e: any) {
    e.preventDefault()
    const file = await dataUrlToFile(croppedImage, fileName)
    Resizer.imageFileResizer(file, 400, 400, 'PNG', 0, 0, async uri => {
      const resizedFile = await dataUrlToFile(uri as string, fileName)
      if (!onSubmit) return
      onSubmit(resizedFile)
    })

    setCroppedImage(null)
  }

  return (
    <Stack>
      {dataUrl ? (
        <Stack>
          <Stack
            position='relative'
            minHeight='25rem'
          >
            <Cropper
              image={dataUrl}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape='round'
              showGrid={false}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
          </Stack>
          <EFFooterDialogWrapper>
            <EFButton
              className='button-text'
              sx={{ mr: 1 }}
              onClick={() => setDataUrl(undefined)}
            >
              {t('MessageButton_Cancel')}
            </EFButton>
            <EFButton
              sx={{ mr: 1 }}
              onClick={onChangeImage}
              isLoading={isSaving}
            >
              {t('Button_Save')}
            </EFButton>
          </EFFooterDialogWrapper>
        </Stack>
      ) : (
        <Stack>
          <Dropzone
            fileTypes={['jpg', 'jpeg', 'png']}
            multiple={false}
            onFileUpload={onDrop}
          >
            <Stack className={cx('wrapper-import-file', theme)}>
              <div>
                <span style={{ fontSize: 'var(--fs-md)' }}>
                  {t('Drop_tk0_HereToUploadOr', { 0: 'image' })}
                </span>
                <EFButton
                  id='browseFileBtn'
                  sx={{ my: 2 }}
                >
                  {t('Browse')}
                </EFButton>

                <EFTypography
                  fontStyle='italic'
                  mt={1}
                  fontSize='0.9rem'
                >
                  {t('AcceptableFileExtensionsAre_tk0_', {
                    0: ': .jpg, .jpeg, .png'
                  })}
                </EFTypography>
              </div>
            </Stack>
          </Dropzone>
          <EFFooterDialogWrapper>
            <EFButton
              className='button-text'
              onClick={() => handleClose()}
            >
              {t('MessageButton_Cancel')}
            </EFButton>
          </EFFooterDialogWrapper>
        </Stack>
      )}
    </Stack>
  )
}
